/**
 * THE SIGN-IN WIRE, AND THE ONLY PLACE IT IS KNOWN.
 *
 * Paths and request bodies are typed from `src/api/generated/pharmos-api.ts`,
 * so a revision to the backend's document fails here at compile time rather
 * than inside a screen. The RESPONSE is validated with zod as well, because the
 * document declares every payload member optional (`payload?`, `authToken?`)
 * and a screen cannot be written against "possibly absent everything": a body
 * that does not match is a CONTRACT error, which is a truer thing to say than
 * rendering half a session.
 *
 * THE ENVELOPE. Every success is `{ success, code, message, payload }`. The
 * envelope's own `code`/`message` are the backend's, not ours; nothing here
 * switches on `code`. The screen prints `message` (`docs/DECISIONS.md`, 24-09-2026).
 *
 * THE ORDER, from the document's own descriptions:
 *   1. POST /v1/user/login              → interim token, the branches, isPasswordExpired
 *   2. POST /v1/user/login/reset-password   (only when isPasswordExpired)
 *   3. POST /v1/user/login/branches/{code}  → the working token
 *
 * EVERY PASSWORD FIELD IS SENT ENCRYPTED (`encryptPassword`, `api/password-cipher.ts`):
 * the plain password lives only in the form that typed it and this call.
 * THE LOGIN PASSWORD IS THE ONLY ONE WITH A TIMESTAMP sealed in front of it.
 * The reset's new password, and both fields of a change, are the password alone.
 */
import { z } from "zod";
import { accountStatusSchema, displayLangSchema, screenLayoutSchema } from "@/api/account";
import { branchProgrammesSchema } from "@/api/branch-programme";
import { createApiClient } from "@/api/client";
import { encryptPassword } from "@/api/password-cipher";
import { AppError } from "@/api/problems";
import type { paths } from "@/api/generated/pharmos-api";
import type { DisplayLang, ScreenLayout } from "@/api/preference";

/**
 * Built per call. `apiBaseUrl()` is read then, which is what lets a test stub
 * the host; a client built at import would keep the empty URL from load time.
 */
function client() {
  return createApiClient<paths>();
}

/* ── the document's schemas ──────────────────────────────────────────────── */

const roleSchema = z.object({
  roleCode: z.string().optional(),
  roleNameEn: z.string().optional(),
  roleNameZh: z.string().optional(),
});

const grantedBranchSchema = z.object({
  /** Required here: a branch card without a code cannot be entered. The document leaves it optional (`GAPS.md` B6). */
  branchCode: z.string(),
  branchNameEn: z.string().optional(),
  branchNameZh: z.string().optional(),
  /** Parsed. Not copied onto the session: enrolment is not drawn (`GAPS.md` B9). */
  programmes: branchProgrammesSchema,
  roles: z.array(roleSchema).optional(),
  /**
   * A list of strings as of 25-09-2026 06:49 UTC. Parsed so the body still
   * reads. Not copied into `session.permissions`: the values are not yet
   * known to be this repo's catalogue (`GAPS.md` B5).
   */
  permissions: z.array(z.string()).optional(),
});

const companySchema = z.object({
  companyNameEn: z.string().optional(),
  companyNameZh: z.string().optional(),
  contractStartDate: z.string().optional(),
  contractEndDate: z.string().optional(),
});

const userSchema = z.object({
  staffId: z.string(),
  /**
   * `A` or `I` as of 27-09-2026 14:24 UTC. A value outside the enum is a
   * contract error: the document no longer leaves `status` a bare string.
   */
  status: accountStatusSchema,
  /**
   * The two preferences the account carries, both `required` in the document
   * and so both required here. As of 27-09-2026 14:24 UTC `UserObj` enumerates
   * them the same way the preference write does (`EN|ZH`, `BROWSER|WINGRID`).
   * `localeFromDisplayLang` and `viewFromScreenLayout` still map those values;
   * a spelling outside the enum does not reach them.
   */
  displayLang: displayLangSchema,
  screenLayout: screenLayoutSchema,
  /** On `UserObj` as of 24-09-2026. Read only during sign-in, never on a reload. */
  isPasswordExpired: z.boolean().optional(),
  /** On `UserObj` as of 24-09-2026 09:36 UTC. The account carries it; no write in the document sets it. */
  isBlocked: z.boolean().optional(),
  /** On `UserObj` as of 25-09-2026. The roster's order. Not used to sort here. */
  createDate: z.string().optional(),
  staffNameEn: z.string().optional(),
  staffNameZh: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  isPharmacist: z.boolean(),
  pharmacistRegNumber: z.string().optional(),
  employmentFirstDay: z.string().optional(),
  employmentLastDay: z.string().optional(),
});

const loginPayloadSchema = userSchema.extend({
  company: companySchema.optional(),
  branches: z.array(grantedBranchSchema).optional(),
  /** Required here: the interim token is what the reset and the branch exchange send. The document leaves it optional. */
  authToken: z.string(),
});

/**
 * The branch exchange. `authToken` is required here even though the document
 * leaves it optional: a session without a token is not a session. `company`
 * and `branch` stay optional, because the document does not require them.
 */
const loginBranchPayloadSchema = userSchema.extend({
  company: companySchema.optional(),
  branch: grantedBranchSchema.optional(),
  authToken: z.string(),
});

/** The profile read. Same shape, and `authToken` is optional there. */
const profileSchema = userSchema.extend({
  company: companySchema.optional(),
  branch: grantedBranchSchema.optional(),
  authToken: z.string().optional(),
});

export type LoginPayload = z.infer<typeof loginPayloadSchema>;
export type LoginBranchPayload = z.infer<typeof loginBranchPayloadSchema>;
export type GrantedBranch = z.infer<typeof grantedBranchSchema>;
export type UserProfile = z.infer<typeof profileSchema>;

/* ── the envelope ────────────────────────────────────────────────────────── */

/** Pulls `payload` out of the envelope and proves its shape, or says the contract moved. */
function payloadOf<T>(schema: z.ZodType<T>, body: unknown, path: string, status: number): T {
  const payload =
    body && typeof body === "object" && "payload" in body
      ? (body as { payload?: unknown }).payload
      : undefined;
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new AppError("contract", {
      status,
      cause: parsed.error,
      message: `${path} returned a payload this build does not expect`,
    });
  }
  return parsed.data;
}

/* ── the calls ───────────────────────────────────────────────────────────── */

export interface Credentials {
  /** The company id. The document calls it `tenantId` and says it is the company id. */
  tenantId: string;
  /** The document's name for the staff id, as of 24-09-2026. */
  staffId: string;
  password: string;
}

/** Step 1. Proves who you are. The token it returns opens only steps 2 and 3. */
export async function login(credentials: Credentials): Promise<LoginPayload> {
  const body = {
    ...credentials,
    password: await encryptPassword(credentials.password, { stamp: new Date() }),
  };
  const { data, response } = await client().POST("/v1/user/login", { body });
  return payloadOf(loginPayloadSchema, data, "/v1/user/login", response.status);
}

/**
 * Step 2, only when step 1 answered `isPasswordExpired`. Spends the one-time
 * password that is already proven by the interim token, so the new one is the
 * whole body.
 */
export async function resetExpiredPassword(newPassword: string): Promise<LoginPayload> {
  const { data, response } = await client().POST("/v1/user/login/reset-password", {
    body: { newPassword: await encryptPassword(newPassword) },
  });
  return payloadOf(loginPayloadSchema, data, "/v1/user/login/reset-password", response.status);
}

/** Step 3. Binds the session to one branch and returns the working token, plus the company and that branch when the server sends them. */
export async function chooseBranch(branchCode: string): Promise<LoginBranchPayload> {
  const { data, response } = await client().POST("/v1/user/login/branches/{branchCode}", {
    params: { path: { branchCode } },
  });
  return payloadOf(loginBranchPayloadSchema, data, "/v1/user/login/branches/{branchCode}", response.status);
}

/** Ends the session server-side. The caller clears the token whatever this answers. */
export async function logout(): Promise<void> {
  await client().POST("/v1/user/logout", {});
}

/**
 * The account behind the current token, and — when the server sends them —
 * the company and the chosen branch. A branch may carry `permissions`,
 * a list of strings. Those values are not yet the session's permission
 * list, and there is still no expiry
 * (`docs/features/settings/GAPS.md` B5). `isPasswordExpired` on this
 * read is not a reason to open the new-password step.
 */
export async function readProfile(): Promise<UserProfile> {
  const { data, response } = await client().GET("/v1/user/profile", {});
  return payloadOf(profileSchema, data, "/v1/user/profile", response.status);
}

/** Changes a password the operator already knows. Not part of signing in. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const [current, next] = await Promise.all([encryptPassword(currentPassword), encryptPassword(newPassword)]);
  await client().PUT("/v1/user/password", { body: { currentPassword: current, newPassword: next } });
}

/**
 * The two preferences that belong to the PERSON — their language and their
 * screen layout.
 *
 * BOTH ARE SENT EVERY TIME BECAUSE THE DOCUMENT REQUIRES BOTH. There is no way
 * to say "change my language and leave my layout alone", so every caller has
 * to know the current value of the one it is not changing and send it back
 * unchanged. That is a read-modify-write with no compare-and-set under it: two
 * terminals changing different preferences in the same minute, and the second
 * save silently undoes the first.
 *
 * Accepted as it stands (`GAPS.md` B6, 21-09-2026: "fine, and absorbed").
 * These are one person's own two settings, changed by hand, so the race needs
 * the same person at two terminals in the same second. A re-read before every
 * write would double the requests to narrow a race it still could not close,
 * so the caller passes the whole pair, and `app-header.tsx` takes the one it
 * is not changing off the session it already holds.
 */
export async function savePreference(preference: {
  displayLang: DisplayLang;
  screenLayout: ScreenLayout;
}): Promise<void> {
  await client().PUT("/v1/user/preference", { body: preference });
}
